import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Search, Send, RefreshCw, Loader2, CheckCheck, Mail, Archive, ArchiveRestore, Trash2, X, ChevronLeft, ImagePlus } from "lucide-react";
import { API } from "../config";
import { compressImage, uploadWithProgress } from "../lib/imageUpload";

const authHeaders = () => ({ "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("session_token")}` });
const keyOf = (it) => `${it.ai_profile.user_id}|${it.user.user_id}`;
const hm = (d) => d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });

export const fmtShort = (iso) => {
  if (!iso) return "";
  const d = new Date(iso), now = new Date(), y = new Date(now);
  y.setDate(now.getDate() - 1);
  if (d.toDateString() === now.toDateString()) return hm(d);
  if (d.toDateString() === y.toDateString()) return `Hier ${hm(d)}`;
  return `${d.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "2-digit" })} ${hm(d)}`;
};
const fmtDay = (iso) => new Date(iso).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

const FILTERS = [["all", "Toutes"], ["unread", "Non lues"], ["read", "Lues"], ["archived", "Archivées"]];

function BulkBar({ count, onAction, onClear, archivedView }) {
  const btn = "flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-medium bg-zinc-800 hover:bg-zinc-700";
  return (
    <div className="flex items-center gap-2 flex-wrap px-3 py-2 bg-pink-950/40 border-y border-pink-900/50" data-testid="admin-bulk-bar">
      <span className="text-xs font-semibold text-pink-300 mr-1" data-testid="admin-bulk-count">{count} sélectionnée(s)</span>
      <button className={btn} onClick={() => onAction("read")} data-testid="bulk-read"><CheckCheck className="w-3.5 h-3.5" />Marquer lu</button>
      <button className={btn} onClick={() => onAction("unread")} data-testid="bulk-unread"><Mail className="w-3.5 h-3.5" />Marquer non lu</button>
      {archivedView
        ? <button className={btn} onClick={() => onAction("unarchive")} data-testid="bulk-unarchive"><ArchiveRestore className="w-3.5 h-3.5" />Désarchiver</button>
        : <button className={btn} onClick={() => onAction("archive")} data-testid="bulk-archive"><Archive className="w-3.5 h-3.5" />Archiver</button>}
      <button className={`${btn} !bg-red-900/60 hover:!bg-red-800 text-red-100`} onClick={() => onAction("delete")} data-testid="bulk-delete"><Trash2 className="w-3.5 h-3.5" />Supprimer</button>
      <button className="ml-auto text-zinc-400 hover:text-white" onClick={onClear} data-testid="bulk-clear"><X className="w-4 h-4" /></button>
    </div>
  );
}

function InboxRow({ it, i, selected, active, onToggle, onOpen }) {
  const unread = it.unread > 0;
  return (
    <div className={`flex items-center gap-2 px-3 py-2.5 border-b border-zinc-900 cursor-pointer transition-colors ${active ? "bg-zinc-800" : selected ? "bg-pink-950/30" : "hover:bg-zinc-900"}`}
      onClick={() => onOpen(it)} data-testid={`inbox-item-${i}`}>
      <input type="checkbox" checked={selected} onClick={(e) => e.stopPropagation()} onChange={() => onToggle(it)}
        className="w-4 h-4 accent-pink-600 shrink-0" data-testid={`inbox-check-${i}`} />
      <div className="relative shrink-0">
        <img src={it.ai_profile.main_photo} alt="" className="w-10 h-10 rounded-full object-cover" />
        {unread && <span className="absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full bg-pink-500 ring-2 ring-black" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className={`text-sm truncate ${unread ? "font-bold text-white" : "text-zinc-300"}`}>{it.user.name} <span className="text-zinc-500 font-normal">→ {it.ai_profile.name}</span></span>
          <span className={`ml-auto text-[11px] shrink-0 ${unread ? "text-pink-400 font-semibold" : "text-zinc-500"}`} data-testid={`inbox-date-${i}`}>{fmtShort(it.last_at)}</span>
        </div>
        <div className="flex items-center gap-2">
          <p className={`text-xs truncate flex-1 ${unread ? "text-zinc-200" : "text-zinc-500"}`}>
            {it.last_message?.sender_id === it.ai_profile.user_id && <span className="text-zinc-500">Vous : </span>}{it.last_message?.content?.startsWith("/api/media/chatimg_") ? "📷 Photo" : it.last_message?.content}
          </p>
          {unread && <span className="bg-pink-600 text-[10px] font-bold rounded-full px-1.5" data-testid={`unread-${i}`}>{it.unread}</span>}
        </div>
      </div>
    </div>
  );
}

function Thread({ thread, onBack, onSent, onOpenMember }) {
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [progress, setProgress] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const endRef = useRef(null);
  const fileRef = useRef(null);

  const sendPhoto = async (file) => {
    if (!file) return;
    setProgress(0);
    const fd = new FormData();
    fd.append("ai_id", thread.ai_profile.user_id);
    fd.append("user_id", thread.user.user_id);
    fd.append("file", await compressImage(file), "photo.jpg");
    const res = await uploadWithProgress(`${API}/admin/reply-photo`, fd, setProgress);
    setProgress(null);
    if (res.ok) onSent(res.data); else toast.error(res.data.detail || "Envoi de la photo impossible");
  };
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [thread]);

  const send = async () => {
    if (!reply.trim()) return;
    setSending(true);
    const r = await fetch(`${API}/admin/reply`, {
      method: "POST", headers: authHeaders(),
      body: JSON.stringify({ ai_id: thread.ai_profile.user_id, user_id: thread.user.user_id, content: reply }),
    });
    setSending(false);
    if (r.ok) { onSent(await r.json()); setReply(""); } else toast.error("Envoi impossible");
  };

  let lastDay = "";
  return (
    <div className="flex-1 flex flex-col min-h-0" data-testid="admin-thread">
      <div className="px-4 py-3 border-b border-zinc-800 flex items-center gap-3">
        <button className="md:hidden text-zinc-400" onClick={onBack} data-testid="admin-thread-back"><ChevronLeft className="w-5 h-5" /></button>
        <button onClick={() => onOpenMember?.(thread.ai_profile.user_id)} data-testid="thread-open-ai"><img src={thread.ai_profile.main_photo} alt="" className="w-11 h-11 rounded-xl object-cover hover:ring-2 hover:ring-pink-600" /></button>
        <div className="text-xs min-w-0 flex-1">
          <button onClick={() => onOpenMember?.(thread.ai_profile.user_id)} className="font-bold text-sm truncate hover:underline block text-left">{thread.ai_profile.name}, {thread.ai_profile.age} <span className="text-pink-500 font-normal">profil IA · {thread.ai_profile.city}</span></button>
          <p className="text-zinc-400 truncate">avec <button onClick={() => onOpenMember?.(thread.user.user_id)} className="font-bold text-blue-300 hover:underline" data-testid="thread-open-member">{thread.user.name}{thread.user.age ? `, ${thread.user.age}` : ""}</button> · {thread.user.email} · {thread.user.city || "—"}</p>
        </div>
        <button onClick={() => onOpenMember?.(thread.user.user_id)} className="px-3 py-1.5 rounded-full bg-blue-600/20 text-blue-300 text-xs font-semibold hover:bg-blue-600/30 shrink-0" data-testid="thread-member-card-btn">Fiche membre</button>
      </div>
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-1.5" style={{ minHeight: "45vh", maxHeight: "62vh" }}>
        {thread.messages.map((m, i) => {
          const fromAi = m.sender_id === thread.ai_profile.user_id;
          const day = m.created_at ? new Date(m.created_at).toDateString() : "";
          const sep = day && day !== lastDay;
          lastDay = day || lastDay;
          return (
            <div key={m.message_id || i}>
              {sep && <div className="text-center text-[11px] text-zinc-500 my-3 capitalize" data-testid={`thread-day-${i}`}>{fmtDay(m.created_at)}</div>}
              <div className={`flex ${fromAi ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[78%] px-3 py-2 rounded-2xl text-sm ${fromAi ? "bg-pink-600 rounded-br-md" : "bg-zinc-800 rounded-bl-md"}`} data-testid={`thread-msg-${i}`}>
                  {m.content_type === "image"
                    ? <img src={m.content} alt="Photo" className="rounded-xl max-h-64 cursor-pointer" onClick={() => setLightbox(m.content)} data-testid={`thread-photo-${i}`} />
                    : <p className="whitespace-pre-wrap break-words">{m.content}</p>}
                  <p className={`mt-0.5 text-[10px] text-right ${fromAi ? "text-pink-200" : "text-zinc-400"}`} data-testid={`thread-msg-time-${i}`}>
                    {m.created_at ? hm(new Date(m.created_at)) : ""}{fromAi && (m.read ? " · Vu" : " · Envoyé")}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>
      {progress !== null && (
        <div className="px-4 pt-2 text-xs text-pink-300" data-testid="admin-photo-progress">
          Envoi de la photo… {progress}%
          <div className="h-1 mt-1 rounded bg-zinc-800"><div className="h-1 rounded bg-pink-600 transition-[width]" style={{ width: `${progress}%` }} /></div>
        </div>
      )}
      <div className="p-3 border-t border-zinc-800 flex items-center gap-2" style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" data-testid="admin-photo-input"
          onChange={(e) => { sendPhoto(e.target.files[0]); e.target.value = ""; }} />
        <button onClick={() => fileRef.current?.click()} disabled={progress !== null} className="p-2 rounded-full bg-zinc-900 hover:bg-zinc-800 disabled:opacity-50" title="Envoyer une photo" data-testid="admin-photo-btn">
          <ImagePlus className="w-5 h-5 text-pink-400" />
        </button>
        <input value={reply} onChange={(e) => setReply(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder={`Répondre en tant que ${thread.ai_profile.name}…`} className="flex-1 bg-zinc-900 rounded-full px-4 py-2.5 text-sm outline-none focus:ring-1 focus:ring-pink-600" data-testid="admin-reply-input" />
        <button onClick={send} disabled={sending} className="bg-pink-600 rounded-full p-2.5 disabled:opacity-50" data-testid="admin-reply-send">
          {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
        </button>
      </div>
      {lightbox && (
        <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4" onClick={() => setLightbox(null)} data-testid="admin-photo-lightbox">
          <img src={lightbox} alt="Photo" className="max-w-full max-h-full object-contain rounded-lg" />
        </div>
      )}
    </div>
  );
}

export default function AdminInbox({ onDenied, onOpenMember, initialFilter = "all" }) {
  const [inbox, setInbox] = useState([]);
  const [filter, setFilter] = useState(initialFilter);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [active, setActive] = useState(null);
  const [thread, setThread] = useState(null);
  const q = useRef({ filter: initialFilter, search: "" });

  const load = async () => {
    const r = await fetch(`${API}/admin/inbox?status=${q.current.filter}&search=${encodeURIComponent(q.current.search)}`, { headers: authHeaders() });
    if (r.status === 403) { onDenied?.(); return; }
    if (r.ok) setInbox(await r.json());
  };
  useEffect(() => {
    load();
    const iv = setInterval(load, 10000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { q.current = { filter, search }; setSelected(new Set()); load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [filter, search]);

  const toggle = (it) => setSelected((s) => { const n = new Set(s); const k = keyOf(it); n.has(k) ? n.delete(k) : n.add(k); return n; });
  const allSelected = inbox.length > 0 && inbox.every((it) => selected.has(keyOf(it)));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(inbox.map(keyOf)));

  const openThread = async (it) => {
    setActive(keyOf(it));
    const r = await fetch(`${API}/admin/thread/${it.ai_profile.user_id}/${it.user.user_id}`, { headers: authHeaders() });
    if (r.ok) setThread(await r.json());
    load();
  };

  const bulk = async (action) => {
    if (action === "delete" && !window.confirm(`Supprimer DÉFINITIVEMENT ${selected.size} conversation(s) ? Les messages seront effacés pour le membre aussi.`)) return;
    const items = [...selected].map((k) => { const [ai_id, user_id] = k.split("|"); return { ai_id, user_id }; });
    const r = await fetch(`${API}/admin/conversations/bulk`, { method: "POST", headers: authHeaders(), body: JSON.stringify({ action, items }) });
    if (!r.ok) { toast.error("Action impossible"); return; }
    const labels = { read: "marquée(s) lue(s)", unread: "marquée(s) non lue(s)", archive: "archivée(s)", unarchive: "désarchivée(s)", delete: "supprimée(s)" };
    toast.success(`${items.length} conversation(s) ${labels[action]}`);
    if ((action === "delete" || action === "archive") && active && selected.has(active)) { setThread(null); setActive(null); }
    setSelected(new Set());
    load();
  };

  return (
    <div className="flex flex-col md:flex-row border-t border-zinc-800" data-testid="admin-inbox">
      <div className={`md:w-[400px] md:border-r border-zinc-800 flex flex-col ${thread ? "hidden md:flex" : "flex"}`}>
        <div className="p-3 flex items-center gap-2">
          <div className="flex-1 flex items-center gap-2 bg-zinc-900 rounded-full px-3 py-1.5">
            <Search className="w-4 h-4 text-zinc-500" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher (nom, email)…"
              className="bg-transparent text-sm flex-1 outline-none" data-testid="admin-search" />
          </div>
          <button onClick={load} data-testid="admin-refresh"><RefreshCw className="w-4 h-4 text-zinc-400 hover:text-white" /></button>
        </div>
        <div className="px-3 pb-2 flex items-center gap-1.5 flex-wrap">
          {FILTERS.map(([k, l]) => (
            <button key={k} onClick={() => setFilter(k)} data-testid={`inbox-filter-${k}`}
              className={`px-3 py-1 rounded-full text-xs ${filter === k ? "bg-pink-600 text-white" : "bg-zinc-900 text-zinc-400 hover:text-white"}`}>{l}</button>
          ))}
        </div>
        <label className="px-3 py-2 flex items-center gap-2 text-xs text-zinc-400 border-t border-zinc-900 cursor-pointer">
          <input type="checkbox" checked={allSelected} onChange={toggleAll} className="w-4 h-4 accent-pink-600" data-testid="inbox-select-all" />
          Tout sélectionner · {inbox.length} conversation(s), les plus récentes en haut
        </label>
        {selected.size > 0 && <BulkBar count={selected.size} onAction={bulk} onClear={() => setSelected(new Set())} archivedView={filter === "archived"} />}
        <div className="overflow-y-auto" style={{ maxHeight: "65vh" }} data-testid="admin-inbox-list">
          {inbox.length === 0 && <p className="text-zinc-500 text-sm p-4">Aucune conversation.</p>}
          {inbox.map((it, i) => (
            <InboxRow key={keyOf(it)} it={it} i={i} selected={selected.has(keyOf(it))} active={active === keyOf(it)} onToggle={toggle} onOpen={openThread} />
          ))}
        </div>
      </div>
      {thread ? (
        <Thread thread={thread} onOpenMember={onOpenMember} onBack={() => { setThread(null); setActive(null); }}
          onSent={(m) => { setThread((t) => ({ ...t, messages: [...t.messages, m] })); load(); }} />
      ) : (
        <div className="hidden md:flex flex-1 items-center justify-center text-zinc-500 p-10" data-testid="admin-thread-empty">Sélectionnez une conversation</div>
      )}
    </div>
  );
}
